import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getModuleEntryBySlug } from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { LoginForm } from '@/pages/auth/LoginForm';
import { clearPlatformOnboardingRequired } from '@/modules/onboarding/onboardingState';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { switchWorkspace } from '@/store/authSlice';
import { getLauncherModules } from '@/utils/moduleAccess';
import { notify } from '@/core/toast/notify';

interface ModuleLoginPageProps {
  slug: string;
}

export function ModuleLoginPage({ slug }: ModuleLoginPageProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const moduleEntry = getModuleEntryBySlug(slug);
  const { isAuthenticated, user, entryModule, workspaceModule, enabledModules, accessibleModules, loading } =
    useAppSelector((s) => s.auth);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!moduleEntry || !isAuthenticated || !user || user.masterAdmin) return;

    const activeModule = entryModule ?? workspaceModule;
    if (activeModule === moduleEntry.erpModule) {
      navigate(moduleEntry.homePath, { replace: true });
      return;
    }

    const canAccess = getLauncherModules(
      enabledModules,
      user.permissions ?? [],
      accessibleModules,
      user.roleCode,
    ).some((entry) => entry.erpModule === moduleEntry.erpModule);

    if (!canAccess) return;

    let cancelled = false;
    setSwitching(true);
    dispatch(switchWorkspace(moduleEntry.erpModule))
      .unwrap()
      .then(() => {
        if (!cancelled) {
          clearPlatformOnboardingRequired();
          navigate(moduleEntry.homePath, { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSwitching(false);
          notify.error(`Could not open ${moduleEntry.label}`);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    moduleEntry,
    isAuthenticated,
    user,
    entryModule,
    workspaceModule,
    enabledModules,
    accessibleModules,
    dispatch,
    navigate,
  ]);

  if (!moduleEntry) {
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }

  if (switching || (isAuthenticated && loading)) {
    return (
      <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
        Opening {moduleEntry.label}…
      </div>
    );
  }

  return (
    <LoginForm
      moduleEntry={moduleEntry}
      showRegisterLink
      showAdminHint
      onLoginSuccess={() => {
        clearPlatformOnboardingRequired();
        navigate(moduleEntry.homePath, { replace: true });
      }}
    />
  );
}
